import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../lib/supabase';
import { parseAnalysisResult } from '../lib/analysisParser';
import { createJournalEntryFromAnalysis } from '../lib/journal';
import { trackEvent } from '../lib/analytics';
import { BRAND } from '../lib/brand';
import stockVestedIconLight from '../assets/brand/stockvested-icon-light.png';
import stockVestedIconDark from '../assets/brand/stockvested-icon-dark.png';
import stockVestedLogoLight from '../assets/brand/stockvested-logo-horizontal-light.png';
import AppBrand from './AppBrand';
import {
  trackProductEvent,
  trackProductEventOncePerSession,
} from '../lib/productEvents';

const RESEARCH_INTENT_OPTIONS = [
  {
    value: 'long_term_investment',
    label: 'Long-term investment',
  },
  {
    value: 'existing_holding',
    label: 'Existing holding',
  },
  {
    value: 'swing_trade',
    label: 'Swing trade',
  },
  {
    value: 'watchlist_research',
    label: 'Watchlist research',
  },
  {
    value: 'exploring',
    label: 'Just exploring',
  },
];

function AnalyzerApp({ session, theme = 'dark', setTheme }) {
  const TRIAL_ANALYSIS_LIMIT = 5;
  const navigate = useNavigate();

  const [ticker, setTicker] = useState('');
  const [loading, setLoading] = useState(false);
  const [loadingTicker, setLoadingTicker] = useState('');
  const [error, setError] = useState('');
  const [usageError, setUsageError] = useState('');
  const [animateResult, setAnimateResult] = useState(false);

  const [tradeHistory, setTradeHistory] = useState([]);
  const [selectedTradeId, setSelectedTradeId] = useState(null);

  const [profile, setProfile] = useState(null);
  const [profileLoading, setProfileLoading] = useState(true);
  const [analysesLoading, setAnalysesLoading] = useState(true);

  const [isMobileHistoryOpen, setIsMobileHistoryOpen] = useState(false);
  const [isMobileAccountOpen, setIsMobileAccountOpen] = useState(false);

  const [isDesktopAccountOpen, setIsDesktopAccountOpen] = useState(false);
  const desktopAccountRef = useRef(null);

  const [journalSaving, setJournalSaving] = useState(false);
  const [journalMessage, setJournalMessage] = useState('');

  const [intentAnalysisId, setIntentAnalysisId] = useState(null);
  const [intentSaving, setIntentSaving] = useState(false);
  const [intentError, setIntentError] = useState('');

  const isDarkMode = theme === 'dark';

  const brandIcon = isDarkMode ? stockVestedIconDark : stockVestedIconLight;
  const brandLogo = stockVestedLogoLight;

  const extractSectionValue = (rawText = '', label = '', nextLabels = []) => {
    const source = String(rawText || '').replace(/\*\*/g, '').trim();
    if (!source || !label) return '';

    const escapeRegex = (value) =>
      String(value).replace(/[.*+?^${}()|[\]\\]/g, '\\$&');

    const escapedLabel = escapeRegex(label);
    const escapedNextLabels = nextLabels.map(escapeRegex);

    const endPattern =
      escapedNextLabels.length > 0
        ? `(?=\\n\\s*(?:${escapedNextLabels.join('|')})\\s*:|$)`
        : '$';

    const regex = new RegExp(
      `(?:^|\\n)\\s*${escapedLabel}\\s*:\\s*([\\s\\S]*?)${endPattern}`,
      'i'
    );

    const match = source.match(regex);
    return match?.[1]?.trim() || '';
};

  const parseBulletLines = (text = '') => {
    return String(text || '')
      .split('\n')
      .map((line) => line.trim())
      .filter(Boolean)
      .map((line) => line.replace(/^[-•]\s*/, '').trim())
      .filter(Boolean);
  };

  const parseTechnicalLines = (text = '') => {
    return parseBulletLines(text).map((line) => {
      const [label, ...rest] = line.split(':');
      const value = rest.join(':').trim();

      return {
        label: value ? label.trim() : 'Context',
        value: value || line,
      };
    });
  };

  const renderBulletList = (items = []) => {
    if (!Array.isArray(items) || items.length === 0) return null;

    return (
      <ul className="research-bullet-list">
        {items.map((item, index) => (
          <li key={`${item}-${index}`}>{item}</li>
        ))}
      </ul>
    );
  };

  const toggleTheme = useCallback(() => {
    if (!setTheme) return;
    setTheme((prev) => (prev === 'dark' ? 'light' : 'dark'));
  }, [setTheme]);

  useEffect(() => {
    trackEvent('analyzer_opened');
  }, []);

  useEffect(() => {
    if (!animateResult) return;

    const timer = setTimeout(() => {
      setAnimateResult(false);
    }, 1800);

    return () => clearTimeout(timer);
  }, [animateResult]);

  const isTrialExpired = useCallback((profileData) => {
    if (!profileData?.trial_ends_at) return false;
    return new Date(profileData.trial_ends_at).getTime() < Date.now();
  }, []);

  const hasTrialUsageRemaining = useCallback((profileData) => {
    if (!profileData) return false;
    return (profileData.trial_analyses_used || 0) < TRIAL_ANALYSIS_LIMIT;
  }, []);

  const canRunAnalysis = useCallback(
    (profileData) => {
      if (!profileData) return false;

      if (profileData.plan_status === 'active') return true;

      if (profileData.plan_status === 'trial') {
        return !isTrialExpired(profileData) && hasTrialUsageRemaining(profileData);
      }

      return false;
    },
    [isTrialExpired, hasTrialUsageRemaining]
  );

  const getRemainingTrialAnalyses = useCallback((profileData) => {
    if (!profileData) return 0;
    return Math.max(
      0,
      TRIAL_ANALYSIS_LIMIT - (profileData.trial_analyses_used || 0)
    );
  }, []);

  const getUsageMessage = useCallback(
    (profileData) => {
      if (!profileData) return '';

      if (profileData.plan_status === 'active') return '';

      if (isTrialExpired(profileData)) {
        return 'Your free trial has expired. Upgrade to continue running new analyses.';
      }

      if (!hasTrialUsageRemaining(profileData)) {
        return 'You’ve used all 5 trial analyses. Upgrade to continue running new analyses.';
      }

      return '';
    },
    [hasTrialUsageRemaining, isTrialExpired]
  );

  const normalizeProfile = useCallback(
    (profileData) => {
      if (!profileData) return null;

      return {
        ...profileData,
        email: profileData.email || session.user.email || '',
        plan_status: profileData.plan_status || 'trial',
        trial_analyses_used: profileData.trial_analyses_used || 0,
      };
    },
    [session.user.email]
  );

  const ensureProfile = useCallback(async () => {
    setProfileLoading(true);

    const { data, error: loadError } = await supabase
      .from('profiles')
      .select('*')
      .eq('id', session.user.id)
      .maybeSingle();

    if (loadError) {
      console.error('Load profile error:', loadError.message);
      setError('Failed to load profile.');
      setProfile(null);
      setProfileLoading(false);
      return;
    }

    if (data) {
      setProfile(normalizeProfile(data));
      setProfileLoading(false);
      return;
    }

    const insertPayload = {
      id: session.user.id,
      email: session.user.email,
    };

    const { data: createdProfile, error: createError } = await supabase
      .from('profiles')
      .upsert(insertPayload, { onConflict: 'id' })
      .select('*')
      .single();

    if (createError) {
      console.error('Create profile error:', createError.message);
      setError('Failed to create profile.');
      setProfile(null);
      setProfileLoading(false);
      return;
    }

    setProfile(normalizeProfile(createdProfile));
    setProfileLoading(false);
  }, [normalizeProfile, session.user.email, session.user.id]);

  const loadAnalyses = useCallback(async () => {
    setAnalysesLoading(true);

    const { data, error: loadError } = await supabase
      .from('analyses')
      .select('*')
      .eq('user_id', session.user.id)
      .order('created_at', { ascending: false });

    if (loadError) {
      console.error('Load analyses error:', loadError.message);
      setError('Failed to load analysis history.');
      setAnalysesLoading(false);
      return;
    }

    const mapped = (data || []).map((item) => ({
    id: item.id,
    ticker: item.ticker,
    result: item.raw_result,
    createdAt: new Date(item.created_at).toLocaleString(),
    createdAtRaw: item.created_at,

    featuresSnapshot: item.features_snapshot || null,

    priceAtAnalysis:
      item.price_at_analysis === null ||
      item.price_at_analysis === undefined
        ? null
        : Number(item.price_at_analysis),

    analysisTimestamp:
      item.analysis_timestamp || item.created_at,

    snapshotVersion: item.snapshot_version || null,
    researchIntent: item.research_intent || null,

    intentPromptStatus:
      item.intent_prompt_status || 'not_asked',
  }));

    setTradeHistory(mapped);

    if (mapped.length > 0) {
      setSelectedTradeId(mapped[0].id);
    } else {
      setSelectedTradeId(null);
    }

    setAnalysesLoading(false);
  }, [session.user.id]);

  const incrementTrialUsage = useCallback(async () => {
    if (!profile || profile.plan_status !== 'trial') return;

    const nextUsed = (profile.trial_analyses_used || 0) + 1;

    const { data, error: updateError } = await supabase
      .from('profiles')
      .update({ trial_analyses_used: nextUsed })
      .eq('id', session.user.id)
      .select('*')
      .single();

    if (updateError) {
      console.error('Increment trial usage error:', updateError.message);
      return;
    }

    setProfile(normalizeProfile(data));
  }, [normalizeProfile, profile, session.user.id]);

  useEffect(() => {
    ensureProfile();
    loadAnalyses();
  }, [ensureProfile, loadAnalyses]);

  useEffect(() => {
    if (analysesLoading || loading || intentAnalysisId) return;

    const pendingAnalysis = tradeHistory.find(
      (trade) => trade.intentPromptStatus === 'pending'
    );

    if (pendingAnalysis) {
      setIntentAnalysisId(pendingAnalysis.id);
    }
  }, [
    analysesLoading,
    loading,
    intentAnalysisId,
    tradeHistory,
  ]);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        desktopAccountRef.current &&
        !desktopAccountRef.current.contains(event.target)
      ) {
        setIsDesktopAccountOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  useEffect(() => {
    if (!profile) {
      setUsageError('');
      return;
    }

    setUsageError(getUsageMessage(profile));
  }, [profile, getUsageMessage]);

  const isMeaningfulAnalysisResult = useCallback((resultText = '') => {
    const trimmed = String(resultText || '').trim();
    if (!trimmed) return false;

    const lower = trimmed.toLowerCase();

    const invalidSignals = [
      'due to the incomplete data provided',
      'please provide complete and valid data',
      'not possible to perform a comprehensive analysis',
    ];

    if (invalidSignals.some((signal) => lower.includes(signal))) {
      return false;
    }

    const parsed = parseAnalysisResult(trimmed);

    const hasNewResearchSnapshot =
      !!parsed.supportReference &&
      !!parsed.structuralChangeLevel &&
      !!parsed.resistanceReference &&
      (
        !!parsed.currentStructure ||
        parsed.structureNotes.length > 0 ||
        parsed.whatToWatch.length > 0
      );

    if (hasNewResearchSnapshot) return true;

    const hasLegacyStructuredFields =
      !!parsed.supportZone &&
      !!parsed.invalidationLevel &&
      !!parsed.resistanceLevel;

    const hasLegacyExplanation =
      !!parsed.explanation && parsed.explanation.length > 40;

    return hasLegacyStructuredFields && hasLegacyExplanation;
  }, []);

    const closeMobilePanels = useCallback(() => {
      setIsMobileHistoryOpen(false);
      setIsMobileAccountOpen(false);
    }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (loading) return;
    if (!ticker.trim()) return;

    const submittedTicker = ticker.toUpperCase().trim();

    setError('');
    setUsageError('');
    setJournalMessage('');

    if (profileLoading || analysesLoading) return;

    if (!canRunAnalysis(profile)) {
      setUsageError(getUsageMessage(profile));
      return;
    }

    trackEvent('analysis_submitted', { ticker: submittedTicker });

    setLoading(true);
    setLoadingTicker(submittedTicker);

    try {
      const response = await fetch(
        `${process.env.REACT_APP_ANALYZE_URL}?ticker=${submittedTicker}`
      );

      const data = await response.json().catch(() => null);

      if (!response.ok) {
        throw new Error(
          data?.error || data?.message || 'Failed to fetch trade analysis.'
        );
      }

      if (!data?.result) {
        throw new Error('The analyzer returned no result.');
      }

      const priceAtAnalysis = Number(data?.priceAtAnalysis);
      const snapshotVersion = Number(data?.snapshotVersion);

      if (
        !data?.snapshot ||
        typeof data.snapshot !== 'object' ||
        !Number.isFinite(priceAtAnalysis) ||
        !Number.isFinite(snapshotVersion)
      ) {
        throw new Error(
          'The analyzer returned an incomplete data snapshot. Please try again.'
        );
      }

      const parsedDebug = parseAnalysisResult(data.result);

      console.log('[AnalyzerApp] Raw analyzer result:', data.result);
      console.log('[AnalyzerApp] Parsed analyzer result:', parsedDebug);
      console.log(
        '[AnalyzerApp] Meaningful result:',
        isMeaningfulAnalysisResult(data.result)
      );

      if (!isMeaningfulAnalysisResult(data.result)) {
        throw new Error(
          'The analyzer could not generate a valid research snapshot for this ticker right now. Please try another ticker or try again later.'
        );
      }

      const { data: inserted, error: insertError } = await supabase
        .from('analyses')
        .insert({
        user_id: session.user.id,
        ticker: submittedTicker,
        raw_result: data.result,

        features_snapshot: data.snapshot,
        price_at_analysis: priceAtAnalysis,

        analysis_timestamp:
          data.analyzedAt ||
          data.snapshot.analyzedAt ||
          new Date().toISOString(),

        snapshot_version: snapshotVersion,
        intent_prompt_status: 'pending',
      })
        .select()
        .single();

      if (insertError) {
        throw insertError;
      }

      const newTrade = {
        id: inserted.id,
        ticker: inserted.ticker,
        result: inserted.raw_result,
        createdAt: new Date(inserted.created_at).toLocaleString(),
        createdAtRaw: inserted.created_at,

        featuresSnapshot: inserted.features_snapshot || null,

        priceAtAnalysis:
          inserted.price_at_analysis === null ||
          inserted.price_at_analysis === undefined
            ? null
            : Number(inserted.price_at_analysis),

        analysisTimestamp:
          inserted.analysis_timestamp || inserted.created_at,

        snapshotVersion: inserted.snapshot_version || null,
        researchIntent: inserted.research_intent || null,

        intentPromptStatus:
          inserted.intent_prompt_status || 'pending',
      };

      setTradeHistory((prev) => [newTrade, ...prev]);
      setSelectedTradeId(newTrade.id);
      setTicker('');
      setAnimateResult(true);

      setIntentError('');
      setIntentAnalysisId(newTrade.id);

      trackEvent('analysis_completed', { ticker: submittedTicker });

      void trackProductEvent({
      userId: session.user.id,
      eventName: 'analysis_completed',
      analysisId: inserted.id,
      ticker: submittedTicker,
      metadata: {
        snapshot_version: inserted.snapshot_version,
        plan_status: profile?.plan_status || 'unknown',

        trial_analysis_number:
          profile?.plan_status === 'trial'
            ? (profile.trial_analyses_used || 0) + 1
            : null,
      },
    });

      if (profile?.plan_status === 'trial') {
        await incrementTrialUsage();
      }
    } catch (err) {
      console.error(err);
      setError(err.message || 'Failed to fetch trade analysis.');
      trackEvent('analysis_failed', {
        ticker: submittedTicker,
        message: err.message || 'unknown_error',
      });
    } finally {
      setLoading(false);
      setLoadingTicker('');
    }
  };

  const handleResearchIntentSelection = async (
  analysis,
  researchIntent
) => {
  if (!analysis?.id || intentSaving) return;

  setIntentSaving(true);
  setIntentError('');

  const selectedAt = new Date().toISOString();

  try {
    const { data, error: updateError } = await supabase
      .from('analyses')
      .update({
        research_intent: researchIntent,
        intent_prompt_status: 'selected',
        intent_selected_at: selectedAt,
      })
      .eq('id', analysis.id)
      .eq('user_id', session.user.id)
      .select()
      .single();

    if (updateError) throw updateError;

    setTradeHistory((previous) =>
      previous.map((trade) =>
        trade.id === analysis.id
          ? {
              ...trade,
              researchIntent: data.research_intent,
              intentPromptStatus: data.intent_prompt_status,
            }
          : trade
      )
    );

    trackEvent('analysis_intent_selected', {
      ticker: analysis.ticker,
      research_intent: researchIntent,
    });

    void trackProductEvent({
      userId: session.user.id,
      eventName: 'analysis_intent_selected',
      analysisId: analysis.id,
      ticker: analysis.ticker,
      metadata: {
        research_intent: researchIntent,
      },
    });

    setIntentAnalysisId(null);
  } catch (error) {
    console.error(error);
    setIntentError(
      'We could not save that answer. Please try again.'
    );
  } finally {
    setIntentSaving(false);
  }
};

const handleResearchIntentSkip = async (analysis) => {
  if (!analysis?.id || intentSaving) return;

  setIntentSaving(true);
  setIntentError('');

  try {
    const { data, error: updateError } = await supabase
      .from('analyses')
      .update({
        research_intent: null,
        intent_prompt_status: 'skipped',
        intent_selected_at: null,
      })
      .eq('id', analysis.id)
      .eq('user_id', session.user.id)
      .select()
      .single();

    if (updateError) throw updateError;

    setTradeHistory((previous) =>
      previous.map((trade) =>
        trade.id === analysis.id
          ? {
              ...trade,
              researchIntent: null,
              intentPromptStatus: data.intent_prompt_status,
            }
          : trade
      )
    );

    trackEvent('analysis_intent_skipped', {
      ticker: analysis.ticker,
    });

    void trackProductEvent({
      userId: session.user.id,
      eventName: 'analysis_intent_skipped',
      analysisId: analysis.id,
      ticker: analysis.ticker,
    });

    setIntentAnalysisId(null);
  } catch (error) {
    console.error(error);
    setIntentError(
      'We could not dismiss the question. Please try again.'
    );
  } finally {
    setIntentSaving(false);
  }
};

  const handleDeleteTrade = async (tradeId) => {
    try {
      const { error: deleteError } = await supabase
        .from('analyses')
        .delete()
        .eq('id', tradeId)
        .eq('user_id', session.user.id);

      if (deleteError) {
        throw deleteError;
      }

      setTradeHistory((prev) => {
        const updated = prev.filter((trade) => trade.id !== tradeId);

        if (selectedTradeId === tradeId) {
          setSelectedTradeId(updated[0]?.id || null);
        }

        return updated;
      });
    } catch (err) {
      console.error(err);
      setError('Failed to delete analysis.');
    }
  };

  const handleAddSnapshotToJournal = async () => {
    if (!selectedTrade) return;

    setJournalSaving(true);
    setJournalMessage('');

    try {
      const existingJournalEntries = await supabase
        .from('journal_entries')
        .select('id')
        .eq('user_id', session.user.id)
        .eq('analysis_id', selectedTrade.id)
        .maybeSingle();

      if (existingJournalEntries.error) {
        throw existingJournalEntries.error;
      }

      if (existingJournalEntries.data) {
        setJournalMessage('This snapshot is already in your journal.');
        return;
      }

      await createJournalEntryFromAnalysis({
      userId: session.user.id,
      source: 'analyzer',
      analysis: {
        id: selectedTrade.id,
        ticker: selectedTrade.ticker,
        raw_result: selectedTrade.result,
        created_at: selectedTrade.createdAtRaw || null,

        features_snapshot: selectedTrade.featuresSnapshot,
        price_at_analysis: selectedTrade.priceAtAnalysis,
        analysis_timestamp: selectedTrade.analysisTimestamp,
        snapshot_version: selectedTrade.snapshotVersion,
        research_intent: selectedTrade.researchIntent,
      },
    });

      setJournalMessage('Saved and ready to track.');
      trackEvent('snapshot_added_to_journal', {
        ticker: selectedTrade.ticker,
      });
    } catch (err) {
      console.error(err);

      if (err?.code === '23505') {
        setJournalMessage('This snapshot is already in your journal.');
      } else {
        setJournalMessage('Failed to add snapshot to journal.');
      }
    } finally {
      setJournalSaving(false);
    }
  };

  const handleLogout = async () => {
    const { error: signOutError } = await supabase.auth.signOut();
    if (signOutError) {
      console.error('Logout error:', signOutError.message);
    }
  };

  const handleUpgradeClick = () => {
    closeMobilePanels();
    setIsDesktopAccountOpen(false);
    navigate('/pricing');
  };

  const handleOpenJournal = () => {
    closeMobilePanels();
    setIsDesktopAccountOpen(false);
    navigate('/journal');
  };

  const handleOpenPerformance = () => {
    closeMobilePanels();
    setIsDesktopAccountOpen(false);
    navigate('/performance');
  };

  const handleSelectTrade = (trade) => {
    if (!trade?.id) return;

    const isReopening = selectedTradeId !== trade.id;

    setSelectedTradeId(trade.id);
    setJournalMessage('');

    if (trade.intentPromptStatus === 'pending') {
      setIntentAnalysisId(trade.id);
    }

    if (isReopening) {
      void trackProductEvent({
        userId: session.user.id,
        eventName: 'analysis_reopened',
        analysisId: trade.id,
        ticker: trade.ticker,
      });
    }
  };

  const handleManageBillingClick = async () => {
    try {
      closeMobilePanels();
      setIsDesktopAccountOpen(false);

      const accessToken = session?.access_token;

      trackEvent('billing_portal_open_attempt');

      const response = await fetch(process.env.REACT_APP_CREATE_PORTAL_URL, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${accessToken}`,
        },
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || 'Failed to open billing portal.');
      }

      if (!data.url) {
        throw new Error('No portal URL returned.');
      }

      window.location.href = data.url;
    } catch (err) {
      console.error(err);
      setError(err.message || 'Unable to open billing portal.');
    }
  };

  const getUserInitials = () => {
    const email = session?.user?.email || '';
    if (!email) return 'U';
    return email.slice(0, 1).toUpperCase();
  };

  const openHistoryDrawer = () => {
    setIsMobileAccountOpen(false);
    setIsMobileHistoryOpen(true);
  };

  const openAccountSheet = () => {
    setIsMobileHistoryOpen(false);
    setIsMobileAccountOpen(true);
  };

  const selectedTrade =
    tradeHistory.find((trade) => trade.id === selectedTradeId) || null;

  const intentAnalysis =
  tradeHistory.find(
    (trade) => trade.id === intentAnalysisId
  ) || null;

  const parsedTrade = useMemo(() => {
    if (!selectedTrade?.result) return null;

    const raw = String(selectedTrade.result || '');
    const parsed = parseAnalysisResult(raw);

    const newFormatLabels = [
      'Support Reference',
      'Structural Change Level',
      'Resistance Reference',
      'Current Structure',
      'Structure Notes',
      'Technical Context',
      'What to Watch',
    ];

    const extractNewField = (label) =>
      extractSectionValue(
        raw,
        label,
        newFormatLabels.filter((item) => item !== label)
      );

    const extractFallback = (labels = []) => {
      for (const label of labels) {
        const escaped = label.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');

        const inlineMatch = raw.match(
          new RegExp(`${escaped}\\s*:\\s*(.+)`, 'i')
        );

        if (inlineMatch?.[1]) return inlineMatch[1].trim();
      }

      return '';
    };

    const explanationFallback = () => {
      const match = raw.match(
        /(AI Explanation|Explanation)\s*:\s*([\s\S]*)/i
      );

      return match?.[2]?.trim() || '';
    };

    const supportReference =
      extractNewField('Support Reference') ||
      parsed.supportZone ||
      extractFallback(['Support Reference', 'Support Zone', 'Entry Zone']) ||
      '—';

    const structuralChangeLevel =
      extractNewField('Structural Change Level') ||
      parsed.invalidationLevel ||
      extractFallback([
        'Structural Change Level',
        'Invalidation Level',
        'Stop Loss',
      ]) ||
      '—';

    const resistanceReference =
      extractNewField('Resistance Reference') ||
      parsed.resistanceLevel ||
      extractFallback([
        'Resistance Reference',
        'Resistance Level',
        'Resistance Target',
        'Target',
      ]) ||
      '—';

    const currentStructure =
      extractNewField('Current Structure') ||
      '';

    const structureNotesRaw =
      extractNewField('Structure Notes') ||
      '';

    const technicalContextRaw =
      extractNewField('Technical Context') ||
      '';

    const whatToWatchRaw =
      extractNewField('What to Watch') ||
      '';

    const legacyExplanation =
      parsed.explanation || explanationFallback() || '';

    return {
      supportReference,
      structuralChangeLevel,
      resistanceReference,
      currentStructure,
      structureNotes: parseBulletLines(structureNotesRaw),
      technicalContext: parseTechnicalLines(technicalContextRaw),
      whatToWatch: parseBulletLines(whatToWatchRaw),
      legacyExplanation,
      hasNewResearchFormat:
        !!currentStructure ||
        !!structureNotesRaw ||
        !!technicalContextRaw ||
        !!whatToWatchRaw,
    };
  }, [selectedTrade]);

  const showWorkspace = tradeHistory.length > 0;
  const isBlocked = !profileLoading && profile && !canRunAnalysis(profile);
    useEffect(() => {
    if (!isBlocked || !profile) return;

    const reason = isTrialExpired(profile)
      ? 'trial_expired'
      : 'analysis_limit_reached';

    void trackProductEventOncePerSession({
      userId: session.user.id,
      eventName: 'paywall_viewed',
      dedupeKey: `paywall_viewed:${reason}`,
      metadata: {
        reason,
        trial_analyses_used: profile.trial_analyses_used || 0,
      },
    });
  }, [
    isBlocked,
    profile,
    isTrialExpired,
    session.user.id,
  ]);
  const desktopPlanText =
    profile?.plan_status === 'active'
      ? 'Pro Plan'
      : `Trial · ${getRemainingTrialAnalyses(profile)} left`;
  const usagePillText =
    !profileLoading && profile
      ? profile.plan_status === 'active'
        ? 'Pro'
        : `Trial: ${getRemainingTrialAnalyses(profile)} / ${TRIAL_ANALYSIS_LIMIT} left`
      : '';

  const initialScreenLoading = profileLoading || analysesLoading;

  const desktopHeader = (
    <div className="desktop-header-actions" ref={desktopAccountRef}>
      <button
        type="button"
        className="desktop-theme-btn"
        onClick={toggleTheme}
        aria-label={isDarkMode ? 'Switch to light mode' : 'Switch to dark mode'}
        title={isDarkMode ? 'Switch to light mode' : 'Switch to dark mode'}
      >
        {isDarkMode ? '☀' : '🌙'}
      </button>

      <button
        type="button"
        className="desktop-secondary-btn journal-top-btn"
        onClick={handleOpenJournal}
      >
        Journal
      </button>

      <button
        type="button"
        className="desktop-secondary-btn"
        onClick={handleOpenPerformance}
      >
        Performance
      </button>

      <button
        type="button"
        className="desktop-account-trigger"
        onClick={() => setIsDesktopAccountOpen((prev) => !prev)}
        aria-expanded={isDesktopAccountOpen}
        aria-label="Open account menu"
      >
        <span className="desktop-account-avatar">{getUserInitials()}</span>
        <span className="desktop-account-meta">
          <span className="desktop-account-plan">{desktopPlanText}</span>
          <span className="desktop-account-email">{session.user.email}</span>
        </span>
        <span className="desktop-account-chevron">
          {isDesktopAccountOpen ? '▴' : '▾'}
        </span>
      </button>

      {isDesktopAccountOpen && (
        <div className="desktop-account-menu">
          <div className="desktop-account-menu-header">
            <div className="desktop-menu-label">Signed in as</div>
            <div className="desktop-menu-email">{session.user.email}</div>
          </div>

          <div className="desktop-account-menu-section">
            <div className="desktop-menu-section-label">Plan</div>
            <div className="desktop-plan-chip">{desktopPlanText}</div>
          </div>

          <div className="desktop-account-menu-section">
            <div className="desktop-menu-section-label">Workspace</div>
            <button
              type="button"
              className="desktop-menu-action"
              onClick={handleOpenJournal}
            >
              Open Journal
            </button>
          </div>

          <div className="desktop-account-menu-section">
            <div className="desktop-menu-section-label">Billing</div>

            {profile?.plan_status === 'active' ? (
              <button
                type="button"
                className="desktop-menu-action"
                onClick={handleManageBillingClick}
              >
                Manage Billing
              </button>
            ) : (
              <button
                type="button"
                className="desktop-menu-action"
                onClick={handleUpgradeClick}
              >
                Upgrade to Pro
              </button>
            )}
          </div>

          <div className="desktop-account-menu-section">
            <div className="desktop-menu-section-label">Account</div>
            <button
              type="button"
              className="desktop-menu-action danger"
              onClick={handleLogout}
            >
              Logout
            </button>
          </div>
        </div>
      )}
    </div>
  );

  if (initialScreenLoading) {
    return (
      <div className={isDarkMode ? 'app dark' : 'app light'}>
        <main className="main-content landing">
          <div className="app-initial-loader">
            <div className="inline-analysis-spinner" />
            <p className="inline-analysis-title">Loading your workspace...</p>
            <p className="inline-analysis-subtext">
              Pulling your profile and saved analyses
            </p>
          </div>
        </main>
      </div>
    );
  }

  return (
    <div className={isDarkMode ? 'app dark' : 'app light'}>
      {(isMobileHistoryOpen || isMobileAccountOpen) && (
        <button
          type="button"
          className="mobile-overlay"
          onClick={closeMobilePanels}
          aria-label="Close mobile panel"
        />
      )}

      <aside className={`sidebar ${showWorkspace ? 'visible' : 'hidden'}`}>
        <div className="sidebar-header">
          <AppBrand theme={theme} />
          <h2>History</h2>
        </div>

        <button
          className="new-trade-btn"
          onClick={() => {
            setSelectedTradeId(null);
            setJournalMessage('');
          }}
        >
          + New Audit
        </button>

        <div className="trade-history-list">
          {tradeHistory.map((trade, index) => (
            <div
              key={trade.id}
              className={`trade-history-row ${
                selectedTradeId === trade.id ? 'active' : ''
              } ${animateResult && index === 0 ? 'fresh-trade' : ''}`}
            >
              <button
                className="trade-history-item"
                 onClick={() => handleSelectTrade(trade)}
              >
                <div className="trade-history-ticker">{trade.ticker}</div>
                <div className="trade-history-time">{trade.createdAt}</div>
              </button>

              <button
                type="button"
                className="trade-delete-btn"
                onClick={() => handleDeleteTrade(trade.id)}
                aria-label={`Delete ${trade.ticker} analysis`}
                title="Delete analysis"
              >
                ×
              </button>
            </div>
          ))}
        </div>
      </aside>

      <div className={`mobile-drawer ${isMobileHistoryOpen ? 'open' : ''}`}>
        <div className="mobile-drawer-header">
          <div>
            <div className="mobile-drawer-title">History</div>
            <div className="mobile-drawer-subtitle">
              Your saved research snapshots
            </div>
          </div>

          <button
            type="button"
            className="mobile-icon-btn secondary"
            onClick={closeMobilePanels}
            aria-label="Close history drawer"
          >
            ×
          </button>
        </div>

        <div className="mobile-drawer-section">
          <button
            type="button"
            className="mobile-drawer-primary-btn"
            onClick={() => {
              setSelectedTradeId(null);
              setJournalMessage('');
              closeMobilePanels();
            }}
          >
            + New Analysis
          </button>
        </div>

        <div className="mobile-drawer-section">
          <div className="mobile-drawer-section-label">Recent Analyses</div>

          <div className="mobile-drawer-history-list">
            {tradeHistory.length === 0 ? (
              <p className="mobile-empty-text">No saved analyses yet.</p>
            ) : (
              tradeHistory.map((trade) => (
                <div key={trade.id} className="mobile-history-row">
                  <button
                    type="button"
                    className={`mobile-history-item ${
                      selectedTradeId === trade.id ? 'active' : ''
                    }`}
                    onClick={() => {
                      handleSelectTrade(trade);
                      closeMobilePanels();
                    }}
                  >
                    <div className="mobile-history-ticker">{trade.ticker}</div>
                    <div className="mobile-history-time">{trade.createdAt}</div>
                  </button>

                  <button
                    type="button"
                    className="mobile-history-delete-btn"
                    onClick={() => handleDeleteTrade(trade.id)}
                    aria-label={`Delete ${trade.ticker} analysis`}
                    title="Delete analysis"
                  >
                    ×
                  </button>
                </div>
              ))
            )}
          </div>
        </div>
      </div>

      <div className={`mobile-account-sheet ${isMobileAccountOpen ? 'open' : ''}`}>
        <div className="mobile-drawer-header">
          <div>
            <div className="mobile-drawer-title">Account</div>
            <div className="mobile-drawer-subtitle">{session.user.email}</div>
          </div>

          <button
            type="button"
            className="mobile-icon-btn secondary"
            onClick={closeMobilePanels}
            aria-label="Close account panel"
          >
            ×
          </button>
        </div>

        {profile && (
          <div className="mobile-account-status-card">
            <div className="mobile-account-status-label">Plan</div>
            <div className="mobile-account-status-value">
              {profile.plan_status === 'active'
                ? 'Pro'
                : `Trial · ${getRemainingTrialAnalyses(profile)} analyses left`}
            </div>
          </div>
        )}

        <div className="mobile-drawer-section">
          <div className="mobile-drawer-section-label">Workspace</div>
          <button
            type="button"
            className="mobile-drawer-action"
            onClick={handleOpenJournal}
          >
            Open Journal
          </button>

          <button
            type="button"
            className="mobile-drawer-action"
            onClick={handleOpenPerformance}
          >
            Open Performance
          </button>
        </div>

        <div className="mobile-drawer-section">
          <div className="mobile-drawer-section-label">Billing</div>

          {profile?.plan_status === 'active' ? (
            <button
              type="button"
              className="mobile-drawer-action"
              onClick={handleManageBillingClick}
            >
              Manage Billing
            </button>
          ) : (
            <button
              type="button"
              className="mobile-drawer-action"
              onClick={handleUpgradeClick}
            >
              Upgrade to Pro
            </button>
          )}
        </div>

        <div className="mobile-drawer-section">
          <div className="mobile-drawer-section-label">Preferences</div>

          <button
            type="button"
            className="mobile-drawer-action"
            onClick={toggleTheme}
          >
            {isDarkMode ? 'Switch to Light Mode' : 'Switch to Dark Mode'}
          </button>
        </div>

        <div className="mobile-drawer-section">
          <div className="mobile-drawer-section-label">Account</div>

          <button
            type="button"
            className="mobile-drawer-action danger"
            onClick={handleLogout}
          >
            Logout
          </button>
        </div>
      </div>

      <main className={`main-content ${showWorkspace ? 'workspace' : 'landing'}`}>
        <div className="mobile-top-bar">
          <button
            type="button"
            className="mobile-icon-btn"
            onClick={openHistoryDrawer}
            aria-label="Open history menu"
          >
            ☰
          </button>

          <div className="mobile-top-bar-title">
            <img
              src={brandIcon}
              alt={`${BRAND.name} logo`}
              className="mobile-brand-icon"
            />
            <span>{BRAND.name}</span>
        </div>

          <div className="mobile-top-bar-right">
            {profile && <div className="mobile-status-pill">{usagePillText}</div>}

            <button
              type="button"
              className="mobile-icon-btn"
              onClick={openAccountSheet}
              aria-label="Open account settings"
            >
              ⚙
            </button>
          </div>
        </div>

        <div className="desktop-only-bar">
          <div className={showWorkspace ? 'top-bar' : 'landing-top-actions'}>
            {desktopHeader}
          </div>
        </div>

        {!showWorkspace && (
          <div className="landing-center">
            <h1 className="landing-heading">{BRAND.product.auditor}</h1>
            <p className="landing-subheading">
              {BRAND.auditorSubtitle}
            </p>

            <form className="search-form" onSubmit={handleSubmit}>
              <div className="search-box">
                <input
                  type="text"
                  value={ticker}
                  onChange={(e) => setTicker(e.target.value.toUpperCase())}
                  placeholder="Enter ticker (e.g., AAPL)"
                  disabled={loading}
                />
                <button
                  type="submit"
                  disabled={loading || !ticker.trim() || isBlocked}
                >
                  {loading ? 'Auditing...' : 'Run Audit'}
                </button>
              </div>
            </form>

            {loading && (
              <div className="inline-analysis-loader">
                <div className="inline-analysis-spinner" />
                <p className="inline-analysis-title">
                  Auditing {loadingTicker || 'ticker'}...
                </p>
                <p className="inline-analysis-subtext">
                  Building your research snapshot
                </p>
              </div>
            )}

            {usageError && (
              <div className="paywall-card">
                <div className="paywall-badge">Free Trial</div>
                <h3 className="paywall-title">
                  Upgrade to keep using Stock Auditor
                </h3>
                <p className="paywall-text">{usageError}</p>

                <div className="paywall-actions">
                  <button
                    type="button"
                    className="upgrade-btn primary"
                    onClick={handleUpgradeClick}
                  >
                    Upgrade Now
                  </button>
                </div>
              </div>
            )}

            {error && <p className="error">{error}</p>}
          </div>
        )}

        {showWorkspace && (
          <div className="workspace-container">
            <form className="workspace-search-form" onSubmit={handleSubmit}>
              <div className="search-box compact">
                <input
                  type="text"
                  value={ticker}
                  onChange={(e) => setTicker(e.target.value.toUpperCase())}
                  placeholder="Enter ticker (e.g., NVDA)"
                  disabled={loading}
                />
                <button
                  type="submit"
                  disabled={loading || !ticker.trim() || isBlocked}
                >
                  {loading ? 'Auditing...' : 'Run Audit'}
                </button>
              </div>
            </form>

            {usageError && (
              <div className="paywall-card compact">
                <div className="paywall-badge">Free Trial</div>
                <h3 className="paywall-title">
                  Upgrade to continue using Stock Auditor
                </h3>
                <p className="paywall-text">{usageError}</p>

                <div className="paywall-actions">
                  <button
                    type="button"
                    className="upgrade-btn primary"
                    onClick={handleUpgradeClick}
                  >
                    Upgrade Now
                  </button>
                </div>
              </div>
            )}

            {error && <p className="error">{error}</p>}

            <div className="analysis-panel">
              {loading ? (
                <div className="inline-analysis-loader">
                  <div className="inline-analysis-spinner" />
                  <p className="inline-analysis-title">
                    Analyzing {loadingTicker || 'ticker'}...
                  </p>
                  <p className="inline-analysis-subtext">
                    Pulling chart context and generating research snapshot
                  </p>
                </div>
              ) : selectedTrade ? (
                <>
                  <div className="analysis-header">
                    <div>
                      <h2>{selectedTrade.ticker}</h2>
                      <p>{selectedTrade.createdAt}</p>
                    </div>
                  </div>

                  <div
                    className={`analysis-card ${
                      animateResult ? 'result-animate' : ''
                    }`}
                  >
                    <div className="metrics-grid">
                      <div className="metric-card">
                        <div className="metric-label">Support</div>
                        <div className="metric-value">
                          {parsedTrade?.supportReference || '—'}
                        </div>
                      </div>

                      <div className="metric-card">
                        <div className="metric-label">
                          Structural Change Level
                        </div>
                        <div className="metric-value">
                          {parsedTrade?.structuralChangeLevel || '—'}
                        </div>
                      </div>

                      <div className="metric-card">
                        <div className="metric-label">Resistance</div>
                        <div className="metric-value">
                          {parsedTrade?.resistanceReference || '—'}
                        </div>
                      </div>
                    </div>

                    <div className="analysis-card-actions">
                      <button
                        type="button"
                        className="analysis-card-action-btn"
                        onClick={handleAddSnapshotToJournal}
                        disabled={!selectedTrade || journalSaving}
                      >
                        {journalSaving
                          ? 'Saving to Journal...'
                          : 'Save & Track This Stock'}
                      </button>
                      <p className="analysis-card-action-helper">
                        Preserve today’s price, levels, and research so you can
                        compare what changes over time.
                      </p>

                      {journalMessage ? (
                        <p
                          className={`analysis-card-action-message ${
                            journalMessage.toLowerCase().includes('failed')
                              ? 'error'
                              : 'success'
                          }`}
                        >
                          {journalMessage}
                        </p>
                      ) : null}
                    </div>

                    <div className="explanation-section">
                      <div className="explanation-title">Research Snapshot</div>

                      {parsedTrade?.hasNewResearchFormat ? (
                        <div className="research-snapshot">
                          {parsedTrade?.currentStructure ? (
                            <div className="research-snapshot-block">
                              <div className="research-snapshot-heading">
                                Current Structure
                              </div>
                              <p className="explanation-body">
                                {parsedTrade.currentStructure}
                              </p>
                            </div>
                          ) : null}

                          {parsedTrade?.structureNotes?.length > 0 ? (
                            <div className="research-snapshot-block">
                              <div className="research-snapshot-heading">
                                Structure Notes
                              </div>
                              {renderBulletList(parsedTrade.structureNotes)}
                            </div>
                          ) : null}

                          {parsedTrade?.whatToWatch?.length > 0 ? (
                            <div className="research-snapshot-block">
                              <div className="research-snapshot-heading">
                                What to Watch
                              </div>
                              {renderBulletList(parsedTrade.whatToWatch)}
                            </div>
                          ) : null}
                        </div>
                      ) : (
                        <p className="explanation-body">
                          {parsedTrade?.legacyExplanation ||
                            'No explanation available.'}
                        </p>
                      )}
                    </div>

                    <div className="analysis-disclaimer">
                      This analysis is for educational purposes only and does not
                      constitute financial advice.
                    </div>
                  </div>
                </>
              ) : (
                <div className="empty-state">
                  <h2>Start a new analysis</h2>
                  <p>Enter a ticker above to generate a research snapshot.</p>
                </div>
              )}
            </div>
          </div>
                )}
      </main>

      {intentAnalysis ? (
        <div className="intent-modal-backdrop">
          <div
            className="intent-modal-card"
            role="dialog"
            aria-modal="true"
            aria-labelledby="research-intent-title"
          >
            <div className="intent-modal-kicker">Quick question</div>

            <h2 id="research-intent-title">
              What are you evaluating with {intentAnalysis.ticker}?
            </h2>

            <p className="intent-modal-copy">
              This helps StockVested understand how people use each
              analysis and improve the research experience.
            </p>

            <div className="intent-option-grid">
              {RESEARCH_INTENT_OPTIONS.map((option) => (
                <button
                  key={option.value}
                  type="button"
                  className="intent-option-btn"
                  onClick={() =>
                    handleResearchIntentSelection(
                      intentAnalysis,
                      option.value
                    )
                  }
                  disabled={intentSaving}
                >
                  {option.label}
                </button>
              ))}
            </div>

            <button
              type="button"
              className="intent-skip-btn"
              onClick={() =>
                handleResearchIntentSkip(intentAnalysis)
              }
              disabled={intentSaving}
            >
              {intentSaving ? 'Saving...' : 'Skip for now'}
            </button>

            {intentError ? (
              <p className="intent-modal-error">{intentError}</p>
            ) : null}
          </div>
        </div>
      ) : null}
    </div>
  );
}

export default AnalyzerApp;